/** Third party imports */
import { DatePipe } from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnInit,
    signal,
    TemplateRef,
    untracked,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, debounceTime, EMPTY, finalize, forkJoin, of, Subject } from 'rxjs';

/** Component imports */
import {
    ButtonComponent,
    FilterOperator,
    NtvTableCellDirective,
    StatsCardComponent,
    StatsData,
    TableComponent,
    TableFilter,
    ToolbarAction,
} from '@shared';
import { FilterOption } from '@shared/components/filter-dropdown/filter-dropdown.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { UserAppToolbarComponent } from './shared';

/** Local imports */
import {
    ApiPagination,
    ApiRole,
    ApiUser,
    ApiUsersAggregatedResponse,
    ApiUsersStats,
    AuthService,
    ExportService,
    SharedService,
    SUPER_ADMIN_ROLE_NAME,
    UsersService,
} from '@core';
import { SubNavbarService } from '@layouts/main-layout/components/sub-navbar/sub-navbar.service';
import { Logger, searchHandler, withLoading } from '@shared/utils';
import {
    USERS_CONTROLS_BUTTONS,
    USERS_FILTER_AUTOCOMPLETE_CONFIG,
    USERS_STATS_CUSTOM_COLORS,
    USERS_TABLE_COLUMNS,
} from './constants';
import { UiTableUser } from './interfaces';
import {
    extractCreatedByUserId,
    formatCreatedByResolvedName,
    getCreatedByDisplayLabel,
} from './users.utils';

@Component({
    selector: 'app-users',
    imports: [
        StatsCardComponent,
        ButtonComponent,
        TableComponent,
        NtvTableCellDirective,
        DatePipe,
        ConfirmationModalComponent,
        UserAppToolbarComponent,
    ],
    templateUrl: './users.component.html',
    styleUrl: './users.component.scss',
})
export class UsersComponent implements OnInit, AfterViewInit {
    /**
     * Canonical stats keys from API response grouped by card type.
     */
    private readonly statsKeys = {
        superAdmin: ['superAdmin'],
        dealer: ['dealer'],
    };

    /**
     * Local cache for resolved role names keyed by roleId.
     * Prevents duplicate role-detail requests for the same role.
     */
    private readonly roleNameByRoleId = new Map<string, string>();

    /**
     * Local cache for resolved creator names keyed by userId.
     * Prevents duplicate user-by-id requests for the same creator.
     */
    private readonly createdByNameByUserId = new Map<string, string>();

    /**
     * Static stats card descriptors used to shape API values into UI cards.
     */
    private readonly usersStatsCards = [
        { title: 'Total Users', description: 'All registered users across the platform' },
        { title: 'Super Admins', description: 'Users with full administrative privileges' },
        { title: 'Dealers', description: 'Primary dealers managing territories' },
    ] as const;

    /**
     * Layout constants
     */
    protected usersStats = signal<StatsData[]>(
        this.usersStatsCards.map((card) => ({
            title: card.title,
            description: card.description,
            total: '',
            stats: [],
        })),
    );
    protected controlsButtons = USERS_CONTROLS_BUTTONS;
    protected usersTableColumns = USERS_TABLE_COLUMNS;
    protected filterAutocompleteConfig = USERS_FILTER_AUTOCOMPLETE_CONFIG;

    /**
     * Loading indicator specifically for search requests triggered
     * from the users toolbar search input.
     *
     * Used to display the main table loader while searching
     * and while clearing the current search keyword.
     */
    protected isSearchingUsers = signal<boolean>(false);

    /**
     * Inject router service for navigation
     */
    private router = inject(Router);

    /**
     * Inject destroy ref to destroy subscriptions and trigger cancellation callbacks
     */
    private destroyRef = inject(DestroyRef);

    /**
     * Inject users service for users-related API calls
     */
    private usersService = inject(UsersService);

    /**
     * Inject auth service for role-based UI visibility.
     */
    private authService = inject(AuthService);

    /**
     * Inject shared service for shared API calls
     */
    private sharedService = inject(SharedService);

    /**
     * Inject sub-navbar service to programamtically project content into its view
     */
    private subNavbarService = inject(SubNavbarService);

    /**
     * Inject service to perform export
     */
    private exportService = inject(ExportService);

    /**
     * Authenticated user traits shape used for role-based UI visibility.
     */
    private readonly currentUserTraits = this.authService.getUserTraits<{
        data?: { roleName?: string };
    }>();

    /**
     * Content reference to project into the sub-navbar component
     */
    protected subNavBarContent = viewChild<TemplateRef<any>>('subNavBarContent');

    /**
     * Table selection
     */
    protected selection = signal<UiTableUser[]>([]);

    /**
     * Holds the mapped UI representation of the users table list.
     */
    protected usersData = signal<UiTableUser[]>([]);

    /**
     * Fixed page size for users requests. Kept separate from `pagination.limit`
     * because that signal gets overwritten by whatever `meta.pagination` the
     * backend echoes back on every fetch — using it directly for "Show Less"
     * truncation would silently drift if the server ever reports a different
     * limit than requested.
     */
    protected readonly pageSize = 10;

    /**
     * Holds the client-types options for filter by dropdown
     */
    protected filterByOptions = signal<FilterOption[]>([]);

    /**
     * Holds the selected user data, either for editing or for deleting
     */
    protected selectedUser = signal<UiTableUser | null>(null);

    /**
     * The debounced search keyword used to filter users by name or email.
     */
    protected searchKey = signal<string>('');

    /**
     * Filter parameters for querying users
     * Uses filter operators to build query conditions
     */
    private filterParams = signal<Record<string, FilterOperator>>({});

    /**
     * Pagination configuration for the users module.
     * Controls the current page, page size limit
     */
    protected pagination = signal<ApiPagination>({
        hasNextPage: true,
        hasPreviousPage: false,
        limit: this.pageSize,
        page: 1,
        totalCount: 0,
        totalPages: 0,
    });

    /**
     * Whether to show delete confirmation modal
     */
    protected showDeleteConfirmationModal = signal<boolean>(false);

    /**
     * Whether to show the successful deletion modal
     */
    protected showSuccessDeleteModal = signal<boolean>(false);

    /**
     * Whether to show the success creation modal (triggered from the create-user tab)
     */
    protected showSuccessCreationModal = signal<boolean>(false);

    /**
     * Subject to handle debounced table state changes (filters, pagination)
     */
    private tableStateSubject = new Subject<TableFilter>();

    /** ================================ Loading Indicators ========================= */

    /**
     * Loading indicator both for loading and searching users
     */
    protected isLoadingUsers = signal<boolean>(false);

    /**
     * Loading indicator for fetching user client types.
     * Kept separate so table loading state is not affected.
     */
    protected isLoadingUserClientTypes = signal<boolean>(false);

    /**
     * Loading indicator for users stats cards.
     */
    protected isLoadingUserStats = signal<boolean>(false);

    /**
     * Loading indicator for filtering users using the table column filters
     */
    protected isColumnFiltering = signal<boolean>(false);

    /**
     * Loading indicator while resolving role names from role IDs.
     */
    protected isResolvingRoles = signal<boolean>(false);

    /**
     * Loading indicator while resolving created-by names from creator user IDs.
     */
    protected isResolvingCreatedBy = signal<boolean>(false);

    /**
     * Loading indicator for deleting users
     */
    protected isDeletingUsers = signal<boolean>(false);

    /**
     * Loading indicator for pagination append ("Show More") requests.
     */
    protected isPaginating = signal<boolean>(false);

    /**
     * Tracks pagination ("Show More") requests so loading uses overlay mode.
     */
    private isPaginationRequest = signal<boolean>(false);

    /**
     * Reactive toolbar buttons
     */
    protected toolbarButtons = computed(() => {
        const selections = this.selection();
        return this.controlsButtons.map((btn) => {
            return {
                ...btn,
                disabled: btn.variant === 'danger' && selections.length <= 1,
            };
        });
    });

    /**
     * Reactive users total count
     */
    protected totalUsersCount = computed(() => this.pagination()?.totalCount ?? 0);

    /**
     * Controls New User button visibility.
     * Hidden when current authenticated role is Dealer.
     */
    protected showNewUserButton = computed(
        () => (this.currentUserTraits?.data?.roleName ?? '') !== 'Dealer',
    );

    /**
     * Row navigation to `/users/:id` is enabled only for Super Admins (matches route guard).
     */
    protected canNavigateToUserDetails = computed(
        () => this.currentUserTraits?.data?.roleName === SUPER_ADMIN_ROLE_NAME,
    );

    /**
     * Trigger for loading users.
     * Only triggers when filter parameters, page, or limit changes.
     * Prevents infinite loop when pagination metadata is updated.
     */
    private loadTrigger = computed(
        () => ({
            filterParams: this.filterParams(),
            page: this.pagination().page,
            limit: this.pageSize,
        }),
        {
            equal: (a, b) => JSON.stringify(a) === JSON.stringify(b),
        },
    );

    constructor() {
        /**
         * Search users based on debounced searchKey
         *
         * @remarks
         * Put it here as per convention since on every searchKey change, we need to re-trigger the searching
         * so we need to cancel the previous request so the latest searchKey will be the only one that will be processed
         *
         * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal} - not really angular but the behind the scene as to why
         */
        searchHandler(
            this.searchKey,
            (searchKey) => {
                const currentPagination = this.getTablePaginationParams();

                return this.usersService
                    .getAll({
                        ...currentPagination,
                        search: searchKey,
                        filters: this.filterParams(),
                    })
                    .pipe(withLoading(this.isSearchingUsers));
            },
            this.destroyRef,
            {
                next: (data) => {
                    this.updateUsersTable(data, false);
                },
                onEmpty: () => {},
                error: (error) => {
                    Logger.error('UsersComponent', `Search went wrong:`, error);
                },
            },
        );

        this.destroyRef.onDestroy(() => {
            this.subNavbarService.clearContent();
        });

        /**
         * Debounce table filter changes to prevent excessive API calls while typing
         */
        this.tableStateSubject
            .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                const filters: Record<string, FilterOperator> = {};
                // Map simple filter values to FilterOperator objects
                Object.entries(event.filters).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && value !== '') {
                        filters[key] = { _eq: value };
                    }
                });

                // Update signals which will trigger the loading effect
                this.filterParams.set(filters);
                this.pagination.update((prev) => ({
                    ...prev,
                    page: event.page,
                    limit: event.pageSize,
                }));
            });

        /**
         * Effect to load users and re-run when filterParams or pagination changes
         */
        effect(() => {
            const trigger = this.loadTrigger();
            untracked(() => {
                const isPaginationRequest = this.isPaginationRequest();
                const isFiltering = Object.values(trigger.filterParams).length > 0;
                const requestTrigger: 'initial' | 'pagination' | 'filter' = isPaginationRequest
                    ? 'pagination'
                    : isFiltering
                      ? 'filter'
                      : 'initial';

                if (isPaginationRequest) {
                    this.isPaginationRequest.set(false);
                }

                this.loadUsers(
                    { page: trigger.page, limit: trigger.limit },
                    trigger.filterParams,
                    requestTrigger,
                );
            });
        });
    }

    /**
     * Fetch user client types on init
     */
    /**
     * @public
     * @description Initializes users page supporting data.
     * @returns void
     */
    public ngOnInit(): void {
        this.loadUserClientTypes();
        this.loadUserStats();
    }

    /**
     * Project the content into the sub-navbar component after view initializes
     */
    /**
     * @public
     * @description Projects toolbar content into sub-navbar after view initialization.
     * @returns void
     */
    public ngAfterViewInit(): void {
        if (this.subNavBarContent()) {
            this.subNavbarService.setContent(this.subNavBarContent()!);
        }
    }

    /**
     * On talble toolbar action  click (the button or controls above the table)
     * @param {ToolbarAction} name The name of the event
     */
    protected onToolbarAction(name: ToolbarAction): void {
        switch (name) {
            case ToolbarAction.BULK_DELETE:
                this.onPreDelete();
                break;
            case ToolbarAction.EXPORT:
                this.exportUsersData();
                break;
            default:
                break;
        }
    }

    /**
     * Handles cell click — navigates to single-user details for Super Admins only.
     *
     * @returns void
     */
    protected onCellClick(cellData: { field: string; value: string; row: UiTableUser }): void {
        if (!this.canNavigateToUserDetails()) {
            return;
        }

        this.router.navigate(['/users', cellData.row.id], {
            state: { userData: cellData.row },
        });
    }

    /**
     * @protected
     * @description Updates selected user rows from table selection changes.
     * @param selectedRows - Selected user rows
     * @returns void
     */
    protected onSelectionChange(selectedRows: UiTableUser[]): void {
        this.selection.set(selectedRows);
    }

    /**
     * Handles table state changes (filtering, sorting, pagination)
     * Maps the table filters to the API filter parameters
     * @param {any} event The table state event
     */
    protected onTableStateChange(event: TableFilter): void {
        const currentFilters = this.filterParams();
        const nextFilters: Record<string, FilterOperator> = {};

        // Extract next filters to compare with current ones
        Object.entries(event.filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                nextFilters[key] = { _eq: value };
            }
        });

        // Determine if filters have changed to decide whether to debounce
        const filtersChanged = JSON.stringify(nextFilters) !== JSON.stringify(currentFilters);

        if (filtersChanged) {
            this.tableStateSubject.next(event);
        } else {
            // Immediate update for pagination if it's just a page/limit change
            this.pagination.update((prev) => ({
                ...prev,
                page: event.page,
                limit: event.pageSize,
            }));
        }
    }

    /**
     * On search change, triggers after a the set debounce time in the table toolbar component
     * @param {string} value The value of the search input
     */
    protected onSearchValueChange(value: string): void {
        const searchValue = value.trim();

        this.pagination.update((prev) => ({
            ...prev,
            page: 1,
        }));

        this.searchKey.set(searchValue);

        if (!searchValue) {
            this.loadUsers({ page: 1, limit: this.pageSize }, this.filterParams(), 'search');
        }
    }

    /**
     * Handles applying date range filter on querying users
     *
     * @param {{start: Date, end: Date}} dates selected in the date range picker
     */
    protected onDatesChange(dates: { start: Date; end: Date }): void {
        // Set filter params for dates
        this.filterParams.set({
            createdAt: {
                _gte: dates.start.toISOString(),
                _lte: dates.end.toISOString(),
            },
        });
    }

    /**
     * Handles filter by option change
     *
     * @param {string} option The option that was selected
     */
    protected onFilterByOptionSelected(option: string): void {
        void option;
        // this.filterParams.set({
        //     clientTypeName: {
        //         _eq: option,
        //     },
        // });
    }

    /**
     * Handles users table actions click
     *
     * @description
     * Performs certain operation based the emitted event, such as `Lock`, `Screenshot`, `Delete`
     *
     */
    protected onActionClick(event: { action: string; row: UiTableUser }): void {
        const { action, row } = event;
        switch (action) {
            case 'Lock':
                break;
            case 'Screenshot':
                break;
            case 'Delete':
                this.selectedUser.set(row);
                this.showDeleteConfirmationModal.set(true);
                break;
            default:
                break;
        }
    }

    /**
     * Handles closing delete confirmation modal.
     * Closes the modal and clears the selectedUser
     */
    protected closeModal(): void {
        this.showDeleteConfirmationModal.set(false);
        this.selectedUser.set(null);
    }

    /**
     * Handles triggering next page request by updating pagination via showMoreRequeseted output
     */
    protected onShowMoreRequested(): void {
        this.isPaginationRequest.set(true);
        this.pagination.update((pagination) => ({
            ...pagination,
            page: pagination.page + 1,
        }));
    }

    /**
     * Collapses the accumulated user rows back to the first page.
     * Resets `pagination.page` so a subsequent "Show More" re-fetches page 2.
     */
    protected onShowLess(): void {
        this.usersData.update((current) => current.slice(0, this.pageSize));
        this.pagination.update((pagination) => ({ ...pagination, page: 1 }));
    }

    /**
     * Handles pre delete operation.
     * Opens the delete confirmation modal if there are selected users
     */
    private onPreDelete(): void {
        const selectedUsers = this.selection();
        if (selectedUsers.length === 0) return;

        this.showDeleteConfirmationModal.set(true);
    }

    /**
     * Handles confirming deleting user.
     * Deletes the selectedUser after confirming and hides the delete confirmation modal and clears the selectedUser
     */
    protected onConfirmDelete(): void {
        const selectedUser = this.selectedUser();
        const selectedUsers = this.selection();

        if (selectedUser) {
            this.deleteUser(selectedUser.id);
            return;
        }

        if (selectedUsers.length > 0) {
            this.bulkDeleteUsers(selectedUsers);
        }
    }

    /**
     * Maps API response to the UI requirements
     * @param {ApiUsersAggregatedResponse} data The aggregated data from multiple endpoints
     */
    private mapDataToUI(data: ApiUsersAggregatedResponse, append = false): void {
        const { users } = data;

        const mappedUsers: UiTableUser[] = users.data.map((user: ApiUser) => ({
            id: user.userId,
            name: `${user.firstName} ${user.lastName}`,
            emailAddress: user.emailAddress,
            role: user.userClientAssignments?.[0]?.roleName || '--',
            clientType: user.userClientAssignments?.[0]?.clientTypeName,
            createdAt: user.createdAt ? new Date(user.createdAt) : 'N/A',
            createdBy: getCreatedByDisplayLabel(user, this.createdByNameByUserId),
        }));

        if (append) {
            this.usersData.update((usersData: UiTableUser[]) => {
                return [...usersData, ...mappedUsers];
            });
        } else {
            this.usersData.set(mappedUsers);
        }

        this.resolveAndApplyUserRoles(users.data);
        this.resolveAndApplyCreatedBy(users.data);
    }

    /**
     * Resolve role names using role IDs from user client assignments
     * and apply them to the currently displayed users table rows.
     *
     * @param {ApiUser[]} users The raw users from API response
     * @returns {void}
     */
    private resolveAndApplyUserRoles(users: ApiUser[]): void {
        const roleIdByUserId = new Map<string, string>();

        users.forEach((user) => {
            const roleId = this.extractRoleId(user);
            if (roleId) {
                roleIdByUserId.set(user.userId, roleId);
            }
        });

        const uniqueRoleIds = [...new Set(roleIdByUserId.values())];

        if (uniqueRoleIds.length === 0) {
            return;
        }

        // Apply already-cached role names immediately.
        this.usersData.update((existingUsers) =>
            existingUsers.map((userRow) => {
                const roleId = roleIdByUserId.get(userRow.id);
                if (!roleId) {
                    return userRow;
                }

                const cachedRoleName = this.roleNameByRoleId.get(roleId);
                if (!cachedRoleName) {
                    return userRow;
                }

                return {
                    ...userRow,
                    role: cachedRoleName,
                };
            }),
        );

        const missingRoleIds = uniqueRoleIds.filter((roleId) => !this.roleNameByRoleId.has(roleId));

        if (missingRoleIds.length === 0) {
            return;
        }

        this.isResolvingRoles.set(true);

        forkJoin(
            missingRoleIds.map((roleId) =>
                this.usersService.getRoleById(roleId).pipe(
                    catchError((error: unknown) => {
                        Logger.error('UsersComponent', `Error fetching role ${roleId}:`, error);
                        return of({
                            data: {
                                roleId,
                                roleCode: '',
                                roleName: 'N/A',
                            } as ApiRole,
                        });
                    }),
                ),
            ),
        )
            .pipe(
                finalize(() => this.isResolvingRoles.set(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((roleResponses) => {
                roleResponses.forEach((response) => {
                    const roleData = response.data as ApiRole | undefined;
                    if (roleData?.roleId) {
                        this.roleNameByRoleId.set(roleData.roleId, roleData.roleName ?? 'N/A');
                    }
                });

                this.usersData.update((existingUsers) =>
                    existingUsers.map((userRow) => {
                        const roleId = roleIdByUserId.get(userRow.id);
                        if (!roleId) {
                            return userRow;
                        }

                        return {
                            ...userRow,
                            role: this.roleNameByRoleId.get(roleId) ?? 'N/A',
                        };
                    }),
                );
            });
    }

    /**
     * Resolves creator names using the `createdBy` user IDs and applies them to table rows.
     *
     * @param {ApiUser[]} users The raw users from API response
     * @returns {void}
     */
    private resolveAndApplyCreatedBy(users: ApiUser[]): void {
        const createdByUserIdByUserId = new Map<string, string>();

        users.forEach((user) => {
            const creatorUserId = extractCreatedByUserId(user);
            if (creatorUserId) {
                createdByUserIdByUserId.set(user.userId, creatorUserId);
            }
        });

        const uniqueCreatorIds = [...new Set(createdByUserIdByUserId.values())];

        if (uniqueCreatorIds.length === 0) {
            return;
        }

        // Apply already-cached creator names immediately.
        this.usersData.update((existingUsers) =>
            existingUsers.map((userRow) => {
                const creatorId = createdByUserIdByUserId.get(userRow.id);
                if (!creatorId) {
                    return userRow;
                }

                const cachedCreatorName = this.createdByNameByUserId.get(creatorId);
                if (!cachedCreatorName) {
                    return userRow;
                }

                return {
                    ...userRow,
                    createdBy: cachedCreatorName,
                };
            }),
        );

        const missingCreatorIds = uniqueCreatorIds.filter(
            (creatorId) => !this.createdByNameByUserId.has(creatorId),
        );

        if (missingCreatorIds.length === 0) {
            return;
        }

        this.isResolvingCreatedBy.set(true);

        forkJoin(
            missingCreatorIds.map((creatorId) =>
                this.usersService.getUserById(creatorId).pipe(
                    catchError((error: unknown) => {
                        Logger.error(
                            'UsersComponent',
                            `Error fetching creator ${creatorId}:`,
                            error,
                        );
                        return of({
                            data: {
                                userId: creatorId,
                                firstName: 'N/A',
                                lastName: '',
                            } as ApiUser,
                        });
                    }),
                ),
            ),
        )
            .pipe(
                finalize(() => this.isResolvingCreatedBy.set(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((creatorResponses) => {
                creatorResponses.forEach((response) => {
                    const creatorData = response.data as ApiUser | undefined;
                    if (!creatorData?.userId) {
                        return;
                    }

                    const fullName = formatCreatedByResolvedName(
                        creatorData.firstName,
                        creatorData.lastName,
                    );
                    this.createdByNameByUserId.set(creatorData.userId, fullName);
                });

                this.usersData.update((existingUsers) =>
                    existingUsers.map((userRow) => {
                        const creatorId = createdByUserIdByUserId.get(userRow.id);
                        if (!creatorId) {
                            return userRow;
                        }

                        return {
                            ...userRow,
                            createdBy: this.createdByNameByUserId.get(creatorId) ?? 'N/A',
                        };
                    }),
                );
            });
    }

    /**
     * Extracts the role ID from a user client assignment.
     *
     * @param {ApiUser} user The user entity with optional assignments
     * @returns {string | null} The role ID, if available
     */
    private extractRoleId(user: ApiUser): string | null {
        const roleIdValue = user.userClientAssignments?.[0]?.roleId;

        if (!roleIdValue) {
            return null;
        }

        return typeof roleIdValue === 'string' ? roleIdValue : null;
    }

    /**
     * Updates users table rows and pagination metadata from the latest API response.
     *
     * @param {ApiUsersAggregatedResponse['users']} usersResponse The users API response payload
     * @param {boolean} append Whether to append rows to existing records
     * @returns {void}
     */
    private updateUsersTable(
        usersResponse: ApiUsersAggregatedResponse['users'],
        append: boolean,
    ): void {
        const latestPagination = usersResponse.meta.pagination;
        const currentPagination = this.pagination();

        if (JSON.stringify(latestPagination) !== JSON.stringify(currentPagination)) {
            this.pagination.set(latestPagination);
        }

        this.mapDataToUI({ users: usersResponse }, append);
    }

    /**
     * Returns safe pagination params for users list requests.
     * Ensures only page/limit are sent to the API.
     *
     * @returns {{ page: number; limit: number }} Normalized list pagination params
     */
    private getTablePaginationParams(): { page: number; limit: number } {
        return {
            page: this.pagination().page,
            limit: this.pageSize,
        };
    }

    /**
     * Loads users stats cards data from API and maps counts as whole numbers.
     *
     * @returns {void}
     */
    private loadUserStats(): void {
        this.usersService
            .getStats()
            .pipe(
                withLoading(this.isLoadingUserStats),
                catchError((error: unknown) => {
                    Logger.error('UsersComponent', 'Error fetching users stats: ', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((response) => {
                this.usersStats.set(this.mapStatsData(response.data));
            });
    }

    /**
     * Maps API users stats payload into stats cards values.
     *
     * @param {ApiUsersStats} apiStats Users stats payload from API
     * @returns {StatsData[]} Stats card data with whole-number totals
     */
    private mapStatsData(apiStats: ApiUsersStats): StatsData[] {
        const totalUsers = apiStats.all?.overall ?? 0;
        const superAdminStats = this.resolveStatsBucket(apiStats, this.statsKeys.superAdmin);
        const dealerStats = this.resolveStatsBucket(apiStats, this.statsKeys.dealer);

        const superAdmins = superAdminStats.overall;
        const dealers = dealerStats.overall;

        return this.usersStatsCards.map((card) => {
            switch (card.title) {
                case 'Total Users':
                    return {
                        ...card,
                        iconKey: 'users',
                        customColor: USERS_STATS_CUSTOM_COLORS['totalUsers'],
                        total: String(totalUsers),
                        stats: [
                            {
                                label: 'Super Admins',
                                value: String(superAdmins),
                                colorClass: 'text-green-600',
                                variant: 'normal',
                            },
                            {
                                label: 'Dealers',
                                value: String(dealers),
                                colorClass: 'text-green-600',
                                variant: 'normal',
                            },
                        ],
                    };
                case 'Super Admins':
                    return {
                        ...card,
                        iconKey: 'shield',
                        customColor: USERS_STATS_CUSTOM_COLORS['superAdmins'],
                        total: String(superAdmins),
                        stats: [
                            {
                                label: 'Last Week',
                                value: String(superAdminStats.lastWeek),
                                colorClass: 'text-green-600',
                                variant: 'normal',
                            },
                            {
                                label: 'This Week',
                                value: String(superAdminStats.thisWeek),
                                colorClass: 'text-green-600',
                                variant: 'normal',
                            },
                        ],
                    };
                case 'Dealers':
                    return {
                        ...card,
                        iconKey: 'dealers',
                        customColor: USERS_STATS_CUSTOM_COLORS['dealers'],
                        total: String(dealers),
                        stats: [
                            {
                                label: 'Last Week',
                                value: String(dealerStats.lastWeek),
                                colorClass: 'text-green-600',
                                variant: 'normal',
                            },
                            {
                                label: 'This Week',
                                value: String(dealerStats.thisWeek),
                                colorClass: 'text-green-600',
                                variant: 'normal',
                            },
                        ],
                    };
                default:
                    return card;
            }
        });
    }

    /**
     * Resolves the `overall` count from one of the provided stats keys.
     *
     * @param {ApiUsersStats} stats Stats payload
     * @param {string[]} keys Candidate keys to check in order
     * @returns {number} Overall count or zero if missing
     */
    private resolveOverallCount(stats: ApiUsersStats, keys: string[]): number {
        for (const key of keys) {
            const value = stats[key]?.overall;
            if (typeof value === 'number') {
                return value;
            }
        }

        return 0;
    }

    /**
     * Resolves a stats bucket from one of the provided keys.
     *
     * @param {ApiUsersStats} stats Stats payload
     * @param {string[]} keys Candidate keys to check in order
     * @returns {{ thisWeek: number; lastWeek: number; twoWeeksAgo: number; overall: number }} Normalized bucket
     */
    private resolveStatsBucket(
        stats: ApiUsersStats,
        keys: string[],
    ): { thisWeek: number; lastWeek: number; twoWeeksAgo: number; overall: number } {
        for (const key of keys) {
            const bucket = stats[key];
            if (bucket) {
                return {
                    thisWeek: bucket.thisWeek ?? 0,
                    lastWeek: bucket.lastWeek ?? 0,
                    twoWeeksAgo: bucket.twoWeeksAgo ?? 0,
                    overall: bucket.overall ?? 0,
                };
            }
        }

        return {
            thisWeek: 0,
            lastWeek: 0,
            twoWeeksAgo: 0,
            overall: 0,
        };
    }

    /**
     * Create new user on a new page
     */
    protected onCreateNewUser(): void {
        const route = this.router.url;

        window.open(`${route}/create`, '_blank');
    }

    /**
     * Export users data
     */
    private exportUsersData(): void {
        this.exportService.exportToCsv(this.usersData(), 'users');
    }

    /** ======================== Utilities ============================ */

    /**
     * Resolves pre-confirmation module message based on delete action type.
     * Either single delete or bulk-delete
     */
    protected resolveDeleteMessage(): string {
        const selectedUsers = this.selection();
        if (selectedUsers.length === 0) return '';

        return selectedUsers.length === 1
            ? 'Are you sure you want to delete this user?'
            : `Are you sure you want to delete these ${selectedUsers.length} users?`;
    }

    /** ================================= API CALLS ================================ */

    /**
     * Load paginated users, set loading signal, and maps API response to the UI requirement
     *
     * @returns {void}
     */
    private loadUsers(
        pagination: { limit: number; page: number },
        filter: Record<string, FilterOperator>,
        trigger: 'initial' | 'pagination' | 'filter' | 'search' = 'initial',
    ): void {
        const loading =
            trigger === 'initial'
                ? this.isLoadingUsers
                : trigger === 'pagination'
                  ? this.isPaginating
                  : trigger === 'search'
                    ? this.isSearchingUsers
                    : this.isColumnFiltering;

        forkJoin({
            users: this.usersService.getAll({
                ...pagination,
                filters: filter,
                search: this.searchKey(),
            }),
        })
            .pipe(
                withLoading(loading),
                catchError((error: unknown) => {
                    Logger.error('UsersComponent', 'Error fetching users: ', error);
                    return EMPTY;
                }),

                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (data: ApiUsersAggregatedResponse) => {
                    this.updateUsersTable(data.users, pagination.page > 1);
                },
            });
    }

    /**
     * Retrieves all user client types for the filter by user dropdown
     */
    private loadUserClientTypes(): void {
        this.sharedService
            .getAllClientTypes()
            .pipe(
                withLoading(this.isLoadingUserClientTypes),
                catchError((error: unknown) => {
                    Logger.error('UsersComponent', 'Error fetching user client types: ', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (data) => {
                    this.filterByOptions.set(
                        data.data.map((clientType) => ({ label: clientType.clientTypeName })),
                    );
                },
            });
    }

    /**
     * Delete single user
     *
     * @param {string} userId The userId to delete (uuid)
     * @returns {void}
     */
    private deleteUser(userId: string): void {
        this.usersService
            .delete(userId)
            .pipe(
                withLoading(this.isDeletingUsers),
                catchError((error: unknown) => {
                    Logger.error('UsersComponent', 'Error deleting user: ', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: () => {
                    this.showDeleteConfirmationModal.set(false);
                    this.selectedUser.set(null);
                    this.selection.set([]);
                    this.openSuccessDeleteModal();
                    this.reloadUsersAfterDelete();
                },
            });
    }

    /**
     * Bulk selected dealers
     *
     * @param {UiUser[]} users The bulk-selected users to delete
     * @returns {void}
     */
    private bulkDeleteUsers(users: UiTableUser[]): void {
        const userIds: string[] = users.map((user) => user.id);
        this.usersService
            .bulkDelete(userIds)
            .pipe(
                withLoading(this.isLoadingUsers),
                catchError((error: unknown) => {
                    Logger.error('UsersComponent', 'Error bulk deleting users: ', error);
                    return EMPTY;
                }),
            )
            .subscribe(() => {
                this.showDeleteConfirmationModal.set(false);
                this.selectedUser.set(null);
                this.selection.set([]);
                this.openSuccessDeleteModal();
                this.reloadUsersAfterDelete();
            });
    }

    /**
     * Opens the delete-success modal after current modal close cycle completes.
     * Prevents visual race condition when transitioning between modals.
     *
     * @returns {void}
     */
    private openSuccessDeleteModal(): void {
        queueMicrotask(() => {
            this.showSuccessDeleteModal.set(true);
        });
    }

    /**
     * Reloads the users table after delete operations.
     * Resets to first page so table data is replaced (not appended).
     *
     * @returns {void}
     */
    private reloadUsersAfterDelete(): void {
        const currentPagination = this.pagination();

        if (currentPagination.page !== 1) {
            // Let the existing effect handle reloading when pagination changes.
            this.pagination.set({
                ...currentPagination,
                page: 1,
            });
            return;
        }

        // If already on first page, trigger a direct refresh.
        this.loadUsers(
            {
                page: currentPagination.page,
                limit: currentPagination.limit,
            },
            this.filterParams(),
            'initial',
        );
    }

    /**
     * @description Dismisses the success creation modal
     * @returns void
     */
    protected dismissSuccessCreationModal(): void {
        this.showSuccessCreationModal.set(false);
    }
}
